/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import classnames from 'classnames';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import {
  Modal,
  Row,
  Col,
  Typography,
  Input,
  Button,
  Spin,
  AutoComplete as AutoCompleteInput,
} from 'antd';
import { Form } from '@ant-design/compatible';
import { useLazyQuery, gql } from '@apollo/client';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import EnvironmentFilled from '@ant-design/icons/EnvironmentFilled';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import { pageType } from '~constants/pages';
import OnboardingContext, { onboardingProcesses } from '../context';
import { getCityIdByName } from '~pages/onboarding/queries';
import { googleMapKey } from '~constants';
import event from '~helpers/mixpanel';
import GoogleMaps from './map';
import './_index.scss';

const DEFAULT_ZOOM_LEVEL = 8;
const STREET_ZOOM_LEVEL = 18;
const MODAL_MODE_SEARCH = 'search';
const MODAL_MODE_FORM = 'form';
const CITY_BELONG_CHINA = ['HK', 'TW', 'MO'];
const FOUR_MUNICIPALITY = ['北京市', '上海市', '天津市', '重庆市'];
const LIBRARIES = ['places'];
const DEFAULT_ADDRESS_DATA = {
  city: null,
  cityId: null,
  country: null,
  countryShortName: null,
  street: null,
  zipcode: null,
  address: null,
  currency: null,
  coordinates: {
    lat: 0,
    lng: 0,
  },
};

const MapAddress = ({ form }) => {
  const {
    getFieldDecorator,
    validateFields,
    setFields,
    setFieldsValue,
    getFieldsValue,
    getFieldValue,
    resetFields,
  } = form;

  const { t } = useTranslation();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapKey,
    libraries: LIBRARIES,
    language: 'en-GB',
    region: 'GB',
  });

  const onboardingData = useContext(OnboardingContext);
  const {
    mapAddressData: addressData,
    setMapAddressData: setAddressData,
    propertyTypeSlug,
    process,
  } = onboardingData;

  useEffect(() => {
    if (process === onboardingProcesses.PROPERTY_ADDRESS) {
      event('Viewed OP Property Map Page', { 'Current Page Type': pageType.onBoardingPage });
    }
  }, [process]);

  const autoCompleteInputRef = useRef(null);
  const googleMapRef = useRef(null);

  const [formError, setFormError] = useState(false);
  const [searchPosition, setSearchPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM_LEVEL);
  const [currCenterPos, setCurrCenterPos] = useState(null);
  const [modalMode, setModalMode] = useState(MODAL_MODE_SEARCH);
  const [searchLoading, setSearchLoading] = useState(false);
  const [citySource, setCitySource] = useState([]);

  const [handleValidateCityId] = useLazyQuery(gql(getCityIdByName()?.query), {
    fetchPolicy: 'cache-and-network',
    onCompleted: result => {
      if (result.cities?.edges?.length) {
        const firstMatch = result.cities.edges[0];

        // set City Id
        setAddressData({
          ...addressData,
          cityId: firstMatch?.node?.id,
          currency: firstMatch?.node?.country?.currencyCode,
          countryShortName: firstMatch?.node?.country?.countryCode,
        });
      } else {
        const currCityVal = getFieldValue('city');

        setAddressData(address => ({
          ...address,
          currency: null,
          cityId: null,
        }));
        setFields({
          city: {
            value: currCityVal,
            errors: [new Error(t('landlord.onboarding.map.error.city_notfound'))],
          },
        });
      }
    },
  });

  const [handleGetCities] = useLazyQuery(gql(getCityIdByName()?.query), {
    fetchPolicy: 'cache-and-network',
    onCompleted: result => {
      const source = result?.cities?.edges?.map(({ node }) => {
        const cityId = node?.id;
        const cityName = node?.name;
        const country = node?.country?.name;
        const countryShortName = node?.country?.countryCode;
        const currency = node?.country?.currencyCode;

        return {
          value: `${cityName},${country}`,
          text: `${cityName},${country}`,
          country,
          cityId,
          cityName,
          currency,
          countryShortName,
        };
      });

      setCitySource(source);
    },
  });

  const handleSetAddressData = ({
    address_components,
    formatted_address,
    geometry,
    vicinity,
    name,
  }) => {
    if (address_components && formatted_address && geometry && name) {
      const tempAddressData = {
        country: null,
        city: null,
        cityId: null,
        zipcode: null,
        address: null,
        street: null,
      };

      address_components.forEach(address => {
        if (address.types.includes('country') && CITY_BELONG_CHINA.includes(address.short_name)) {
          tempAddressData.country = 'China';
          tempAddressData.city = address.long_name;
          tempAddressData.countryShortName = address.short_name;
        } else {
          if (address.types.includes('country')) {
            tempAddressData.country = address.long_name;
            tempAddressData.countryShortName = address.short_name;
          }

          if (
            ['postal_town', 'locality'].some(item => address.types.includes(item)) ||
            FOUR_MUNICIPALITY.includes(address.long_name)
          ) {
            tempAddressData.city = address.long_name;
          }
        }

        if (address.types.includes('postal_code')) {
          tempAddressData.zipcode = address.long_name;
        }
      });

      if (!tempAddressData.city) {
        const administrativeArea = address_components?.filter(
          component =>
            component.types?.includes('administrative_area_level_2') ||
            component.types?.includes('administrative_area_level_1'),
        );

        tempAddressData.city = administrativeArea[administrativeArea.length - 1].long_name;
      }

      tempAddressData.address = formatted_address;
      tempAddressData.street = vicinity;
      // tempAddressData.coordinates = geometry.location.toJSON();

      setAddressData(address => ({
        ...address,
        ...tempAddressData,
      }));
      setCurrCenterPos(tempAddressData.coordinates);

      return tempAddressData;
    }
  };

  const handleAutoCompleteLoad = autocomplete => {
    autoCompleteInputRef.current = autocomplete;
  };

  const handleAutoCompleteChanged = () => {
    const inputPlace = autoCompleteInputRef.current?.getPlace();

    const tempAddressData = handleSetAddressData(inputPlace);
    setModalMode(MODAL_MODE_FORM);
    setCitySource([]);

    setFieldsValue(
      {
        city:
          tempAddressData?.city && tempAddressData?.country
            ? `${tempAddressData.city},${tempAddressData.country}`
            : '',
        street: tempAddressData.street,
        zipcode: tempAddressData.zipcode,
      },
      () => {
        handleInputCityBlur();

        setMapZoom(STREET_ZOOM_LEVEL);
      },
    );
  };

  const handleToggleFormMode = () => {
    event('Clicked OP Property Map Page', {
      'Clicked Position': 'Enter manually',
      'Property Type': propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
    setModalMode(MODAL_MODE_FORM);
  };

  const handleReset = () => {
    setFormError(false);

    setSearchPosition(null);
    setMapZoom(DEFAULT_ZOOM_LEVEL);
    setAddressData(DEFAULT_ADDRESS_DATA);
    setCurrCenterPos(googleMapRef.current.defaultCenter);
  };

  const handleResetPin = () => {
    resetFields(['city', 'street', 'zipcode']);
    setCurrCenterPos(searchPosition);
    setAddressData(address => ({
      ...address,
      coordinates: searchPosition,
    }));
  };

  const handleResetAddress = () => {
    autoCompleteInputRef.current.value = '';
    event('Clicked OP Property Map Page', {
      'Clicked Position': 'Reset Address',
      'Property Type': propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
    resetFields(['address', 'country', 'city', 'street', 'zipcode']);

    handleReset();
  };

  const handleReSearchClick = () => {
    setModalMode(MODAL_MODE_SEARCH);
    event('Clicked OP Property Map Page', {
      'Manual Position': 'Back to Search',
      'Property Type': propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
    resetFields(['country', 'city', 'street', 'zipcode']);

    handleReset();
  };

  const handleFindPlace = request => {
    return new Promise((resolve, reject) => {
      if (googleMapRef.current.mapInstance) {
        const service = new window.google.maps.places.PlacesService(
          googleMapRef.current.mapInstance,
        );

        if (request.query) {
          setSearchLoading(true);

          service.findPlaceFromQuery(request, (result, status) => {
            setSearchLoading(false);

            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(result);
            } else {
              reject(status);
            }
          });
        }
      } else {
        reject();
      }
    });
  };

  const handleInputChange = (ev, field) => {
    const tempValue = ev.target.value;

    setAddressData(address => ({
      ...address,
      [field]: tempValue,
      coordinates: {
        lng: 0,
        lat: 0,
      },
    }));
  };

  const handleInputCityChange = currCityVal => {
    let city = currCityVal;

    if (city?.includes(',')) {
      city = city.slice(0, city.lastIndexOf(','));
    }

    if (addressData.city && addressData.country) {
      setAddressData(address => ({
        ...address,
        city: null,
        country: null,
        cityId: null,
      }));
    }

    if (!city) {
      setCitySource([]);

      return;
    }

    handleGetCities({
      variables: {
        nameLike: city,
      },
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputCityBlur = () => {
    let city = getFieldValue('city');

    if (city?.includes(',')) {
      city = city.slice(0, city.lastIndexOf(','));
    }

    if (!city) return;

    handleValidateCityId({
      variables: {
        nameLike: city,
      },
    });
  };

  const handleInputCitySelect = val => {
    const { country, cityId, cityName, currency, countryShortName } =
      citySource.find(item => item.value === val) ?? {};

    setAddressData(address => ({
      ...address,
      country,
      city: val,
      cityId,
      currency,
      countryShortName,
      coordinates: {
        lng: 0,
        lat: 0,
      },
    }));

    setFieldsValue({
      city: `${cityName},${country}`,
    });
  };

  const handleChangePosition = coordinates => {
    if (addressData.coordinates?.lat && addressData.coordinates?.lng) {
      setAddressData(address => ({
        ...address,
        coordinates,
      }));
      setCurrCenterPos(coordinates);
    }
  };

  const handleMapFormSubmit = () => {
    event('Clicked OP Property Map Page', {
      'Manual Position': 'Looks good',
      'Property Type': propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
    validateFields(err => {
      if (!err) {
        const { city, street, zipcode } = getFieldsValue();

        const request = {
          query: `${city} ${street}`,
          fields: ['ALL'],
        };

        handleFindPlace(request)
          .then(result => {
            let tempCity, tempCountry;

            if (city.includes(',')) {
              tempCity = city.slice(0, city.lastIndexOf(','));
              tempCountry = city.slice(city.lastIndexOf(',') + 1);
            }

            let tempAddressData = {
              city: tempCity,
              country: tempCountry,
              street,
              zipcode,
            };

            if (result[0].formatted_address) {
              tempAddressData.address = result[0].formatted_address;
            }

            if (result[0].geometry) {
              tempAddressData.coordinates = result[0].geometry.location.toJSON();

              setCurrCenterPos(tempAddressData.coordinates);
              setSearchPosition(tempAddressData.coordinates);
            }

            setFormError(false);

            setAddressData(address => ({
              ...address,
              ...tempAddressData,
            }));

            setMapZoom(STREET_ZOOM_LEVEL);
            setTimeout(() => {
              setModalMode(MODAL_MODE_SEARCH);
            }, 0);
          })
          .catch(() => {
            setFormError(true);

            setTimeout(() => {
              setFormError(false);
            }, 2000);
          });
      }
    });
  };

  useEffect(() => {
    if (modalMode === MODAL_MODE_SEARCH) {
      setFieldsValue({
        address: addressData.address,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData, modalMode, setFieldsValue]);

  const handleInputCityChangeDebounce = debounce(handleInputCityChange, 300);

  const isAddressReadonly = useMemo(() => {
    const { country, city, cityId, street, zipcode, coordinates } = addressData;

    return country && city && cityId && street && zipcode && coordinates?.lng && coordinates?.lat;
  }, [addressData]);

  const isShowPin = useMemo(() => {
    const { coordinates } = addressData;

    return coordinates?.lng && coordinates?.lat;
  }, [addressData]);

  const modalBodyStyle =
    isShowPin && modalMode === MODAL_MODE_SEARCH
      ? {
          paddingBottom: '80px',
        }
      : {};

  const handleInputCityFocused = () => {
    event('Clicked OP Property Map Page', {
      'Clicked Position': 'Address Blank',
      'Property Type': propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
  };

  return (
    <div className="map">
      <Choose>
        <When condition={isLoaded}>
          <GoogleMaps
            ref={googleMapRef}
            mapZoom={mapZoom}
            setMapZoom={setMapZoom}
            center={currCenterPos}
            position={addressData.coordinates}
            setCenter={setCurrCenterPos}
            changePosition={handleChangePosition}
            propertyTypeSlug={propertyTypeSlug}
          />

          <Modal
            title={null}
            footer={null}
            mask={false}
            width={530}
            visible={true}
            closable={false}
            className="map--modal"
            bodyStyle={modalBodyStyle}
            wrapClassName="map--wrapper"
            getContainer={false}
          >
            <div className="map--modal__title">
              <Typography.Title level={2}>
                {t('landlord.onboarding.map.form.placeholder')}
              </Typography.Title>
            </div>

            <Form>
              <Row
                className={classnames('map--modal__search', {
                  'map--hidden': modalMode === MODAL_MODE_FORM,
                })}
              >
                <Col span={24}>
                  <Autocomplete
                    className="map--modal__autocomplete"
                    onLoad={handleAutoCompleteLoad}
                    onPlaceChanged={handleAutoCompleteChanged}
                  >
                    <Form.Item>
                      {getFieldDecorator(
                        'address',
                        {},
                      )(
                        <Input
                          size="large"
                          ref={autoCompleteInputRef}
                          className="map--modal__address"
                          prefix={<EnvironmentFilled />}
                          placeholder={t('landlord.onboarding.map.input.placeholder')}
                          disabled={isAddressReadonly}
                          onFocus={handleInputCityFocused}
                        />,
                      )}
                    </Form.Item>
                  </Autocomplete>
                </Col>
                <Col span={24}>
                  <Choose>
                    <When condition={getFieldValue('address')}>
                      <Typography.Text
                        underline
                        className="map--modal__link-toggle"
                        onClick={handleResetAddress}
                      >
                        {t('landlord.onboarding.map.button.reset_address')}
                      </Typography.Text>
                    </When>
                    <Otherwise>
                      <Typography.Text
                        underline
                        className="map--modal__link-toggle"
                        onClick={handleToggleFormMode}
                      >
                        {t('landlord.onboarding.map.button.input_manually')}
                      </Typography.Text>
                    </Otherwise>
                  </Choose>
                </Col>
              </Row>

              <If condition={isShowPin}>
                <div
                  className={classnames('map--modal__footer-notice', {
                    'map--hidden': modalMode === MODAL_MODE_FORM,
                  })}
                >
                  <Row type="flex" justify={'center'} align={'middle'}>
                    <Col>
                      <Typography.Text>
                        {t('landlord.onboarding.map.footer.notice')}
                      </Typography.Text>
                      <Button type="link" onClick={handleResetPin}>
                        {t('landlord.onboarding.map.button.reset_pin')}
                      </Button>
                    </Col>
                  </Row>
                </div>
              </If>

              <Row
                gutter={10}
                className={classnames('map--modal__main', {
                  'map--hidden': modalMode === MODAL_MODE_SEARCH,
                })}
              >
                <Col span={24}>
                  <Form.Item label={t('landlord.onboarding.map.form.field.city')}>
                    {getFieldDecorator('city', {
                      rules: [
                        {
                          validator: (rule, value, callback) => {
                            if (!addressData.city) {
                              return callback(t('landlord.onboarding.map.form.city_error'));
                            } else if (!addressData.country) {
                              return callback(t('landlord.onboarding.map.form.country_error'));
                            } else if (!addressData.cityId) {
                              return callback(t('landlord.onboarding.map.error.city_notfound'));
                            } else {
                              return callback();
                            }
                          },
                        },
                      ],
                    })(
                      <AutoCompleteInput
                        size="large"
                        dataSource={citySource}
                        placeholder={t('landlord.onboarding.map.form.input.placeholder')}
                        onSelect={handleInputCitySelect}
                        onChange={handleInputCityChangeDebounce}
                      >
                        <Input
                          onClick={() => {
                            event('Clicked OP Property Map Page', {
                              'Manual Position': 'City',
                              'Property Type': propertyTypeSlug,
                              'Current Page Type': pageType.onBoardingPage,
                            });
                          }}
                        />
                      </AutoCompleteInput>,
                    )}
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('landlord.onboarding.map.form.field.street')}>
                    {getFieldDecorator('street', {
                      rules: [
                        {
                          required: true,
                          message: t('landlord.onboarding.map.form.street_error'),
                        },
                      ],
                    })(
                      <Input
                        size="large"
                        placeholder={t('landlord.onboarding.map.form.input.placeholder')}
                        onChange={ev => handleInputChange(ev, 'street')}
                        onClick={() => {
                          event('Clicked OP Property Map Page', {
                            'Manual Position': 'Street',
                            'Property Type': propertyTypeSlug,
                            'Current Page Type': pageType.onBoardingPage,
                          });
                        }}
                      />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('landlord.onboarding.map.form.field.zipcode')}>
                    {getFieldDecorator('zipcode', {
                      rules: [
                        {
                          required: true,
                          validator: (rule, value, callback) => {
                            if (value && value.length <= 10 && /^[a-zA-Z0-9\-\s]+$/g.test(value)) {
                              callback();
                            } else if (value) {
                              callback(t('landlord.onboarding.map.form.zipcode_invalid'));
                            } else {
                              callback(t('landlord.onboarding.map.form.zipcode_error'));
                            }
                          },
                        },
                      ],
                    })(
                      <Input
                        size="large"
                        placeholder={t('landlord.onboarding.map.form.input.placeholder')}
                        onChange={ev => handleInputChange(ev, 'zipcode')}
                        onClick={() => {
                          event('Clicked OP Property Map Page', {
                            'Manual Position': 'Zipcode',
                            'Property Type': propertyTypeSlug,
                            'Current Page Type': pageType.onBoardingPage,
                          });
                        }}
                      />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Row type="flex" justify="space-between" align="middle">
                    <Col>
                      <Button
                        type="primary"
                        size={'large'}
                        loading={searchLoading}
                        onClick={handleMapFormSubmit}
                      >
                        {t('landlord.onboarding.map.button.looks_good')}
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type="link"
                        onClick={handleReSearchClick}
                        className="map--modal__back-search"
                      >
                        <ArrowLeftOutlined /> {t('landlord.onboarding.map.button.back_search')}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <div
                className={classnames('map--modal__footer-warning', {
                  'map--modal__footer-warning--hidden': !formError,
                })}
              >
                <Row type="flex" justify={'center'} align={'middle'}>
                  <Col>
                    <ExclamationCircleFilled />
                    <Typography.Text>
                      {t('landlord.onboarding.map.error.input_notfound')}
                    </Typography.Text>
                  </Col>
                </Row>
              </div>
            </Form>
          </Modal>
        </When>
        <Otherwise>
          <div className="map--loading">
            <Spin size={'large'} />
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

export default Form.create({
  name: 'map-address',
})(MapAddress);
